import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/lib/api-client';
import { useAuth } from '@/context/AuthContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Lock, ArrowRight, Loader2, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';

/** FR-96: an invited staff member replaces the temporary password before anything else. */
export default function ChangePassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (password.length < 8) {
      setError(t('change_password.too_short'));
      return;
    }
    if (password !== confirmPassword) {
      setError(t('change_password.mismatch'));
      return;
    }

    setLoading(true);
    setError('');
    try {
      const { error: updateError } = await apiClient.PATCH('/me/password', {
        body: { password, password_confirmation: confirmPassword },
      });
      if (updateError) {
        setError(t('change_password.failed'));
        return;
      }
      await refreshUser();
      toast.success(t('change_password.success'));
      navigate('/dashboard', { replace: true });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm animate-fade-in">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-foreground tracking-tight">{t('change_password.title')}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t('change_password.subtitle')}</p>
        </div>

        <div className="mb-4 p-3 rounded-xl border bg-warning/10 border-warning/20 flex gap-2">
          <ShieldAlert size={18} className="text-warning shrink-0 mt-0.5" />
          <p className="text-xs text-foreground">{t('change_password.warning')}</p>
        </div>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">{t('change_password.new_password')}</label>
            <div className="relative">
              <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="password"
                placeholder={t('change_password.placeholder_new')}
                value={password}
                onChange={e => { setPassword(e.target.value); setError(''); }}
                className="h-11 pl-10"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">{t('change_password.confirm_password')}</label>
            <div className="relative">
              <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="password"
                placeholder={t('change_password.placeholder_confirm')}
                value={confirmPassword}
                onChange={e => { setConfirmPassword(e.target.value); setError(''); }}
                className="h-11 pl-10"
              />
            </div>
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <Button onClick={handleSubmit} disabled={loading} className="w-full h-11">
            {loading ? <Loader2 size={16} className="animate-spin mr-2" /> : <ArrowRight size={16} className="mr-2" />}
            {t('change_password.submit')}
          </Button>
        </div>
      </div>
    </div>
  );
}
